/**
 * Utilities for calculating text and watermark contrast in Luminary scenes.
 * Analyzes the contrast between text assets/watermarks and their backgrounds.
 */

import type { AssetGroup, BaseAsset, Scene, TextAsset } from '@lumen5/luminary';
import Luminary, { VideoData } from '@lumen5/luminary';
import type { MediaAsset } from '@lumen5/luminary/core/asset/MediaAsset';

import { isWatermarkAsset } from 'js/src/agent/video-state/serialization/mediaHelpers';
import { getQueryParam } from 'js/src/libs/querystring';
import { awaitCanPlayWithTimeout } from 'js/src/luminary/can-play-utils';
import { getEffectiveAssetBounds, getDrawnAssetBounds } from 'js/src/luminary/luminary-utils';
import type { RGB } from 'js/src/utils/contrast-utils';
import {
    averageColor,
    calculateContrastRatio,
    extractColorsFromImageData,
    isLargeFontSize,
} from 'js/src/utils/contrast-utils';

const DEBUG_CONTRAST = getQueryParam('debugContrast') === 'true';

export enum TextBackdropKind {
    /** A photo or video the text sits directly on. */
    Media = 'media',
    /** A shape the design draws, addressable in its own right. */
    ShapeBackdrop = 'shape_backdrop',
    /** A graphic the text asset draws behind its own glyphs. */
    TextOwnBackground = 'text_own_background',
    /** A graphic an enclosing asset group draws behind its children. */
    GroupBackground = 'group_background',
}

/**
 * What the text is drawn on top of, as far as the asset tree can tell.
 */
export interface TextBackdrop {
    assetId: string;
    assetName: string;
    kind: TextBackdropKind;
}

export interface TextContrastResult {
    contrastRatio: number;
    textColor: RGB;
    backgroundColor: RGB;
    textAssetId: string;
    textAssetName: string;
    textContent: string;
    fontSize: number;
    isLargeText: boolean;
    backdrop: TextBackdrop | null;
}

export interface WatermarkContrastResult {
    contrastRatio: number;
    watermarkColor: RGB;
    backgroundColor: RGB;
    watermarkPosition: string;
    watermarkOpacity: number;
}

/**
 * Chroma A: the fill the text carries on the first probe render. The second render swaps in
 * a different chroma, and a pixel counts as text if and only if it changed between the two.
 */
const CHROMA_A = 16711935; // 0xFF00FF magenta

const CHROMA_CANDIDATES = [
    16711935, // magenta
    65280, // green
    65535, // cyan
    255, // blue
    16776960, // yellow
    16744192, // orange
];

/** Candidates closer than this to chroma A leave too weak a signal to diff against it. */
const MIN_CHROMA_SEPARATION = 200;

/** Per-channel slack when pairing the two renders, to absorb dither and float noise. */
const MASK_EPSILON = 4;

/** Per-channel slack for the legacy "is this pixel the chroma?" test used by the fallback. */
const CHROMA_MATCH_TOLERANCE = 10;

const SAMPLE_RATE = 10;

/**
 * A renderer that is not deterministic between the two probe renders (a video media asset
 * advancing a frame, say) marks everything as text. Past these bounds the mask is not
 * trustworthy and we fall back to filtering by colour distance.
 */
const MAX_TEXT_SAMPLE_FRACTION = 0.6;
const MIN_BACKGROUND_SAMPLES = 16;

/**
 * Converts a decimal color value (e.g. 16777215 for white) to RGB.
 */
function decimalToRgb(decimal: number): RGB {
    return {
        r: Math.floor(decimal / 65536) % 256,
        g: Math.floor(decimal / 256) % 256,
        b: decimal % 256,
    };
}

function colorDistance(a: RGB, b: RGB): number {
    return Math.sqrt((a.r - b.r) ** 2 + (a.g - b.g) ** 2 + (a.b - b.b) ** 2);
}

function isNearColor(color: RGB, target: RGB, tolerance: number): boolean {
    return (
        Math.abs(color.r - target.r) < tolerance &&
        Math.abs(color.g - target.g) < tolerance &&
        Math.abs(color.b - target.b) < tolerance
    );
}

/**
 * Picks the second probe chroma: the candidate that is both far enough from chroma A for the
 * diff to be unambiguous, and as far as possible from every colour already on screen. Keeping
 * it off the scene's palette is what makes the degenerate-mask fallback usable.
 */
function pickSecondChroma(imageData: ImageData): number {
    const chromaA = decimalToRgb(CHROMA_A);
    const sceneColors = extractColorsFromImageData(imageData, SAMPLE_RATE).filter(
        (color) => !isNearColor(color, chromaA, CHROMA_MATCH_TOLERANCE),
    );

    const eligible = CHROMA_CANDIDATES.filter(
        (candidate) => colorDistance(decimalToRgb(candidate), chromaA) >= MIN_CHROMA_SEPARATION,
    );

    let bestChroma = eligible[0];
    let bestSeparation = -1;
    eligible.forEach((candidate) => {
        const candidateRgb = decimalToRgb(candidate);
        const separation = sceneColors.reduce(
            (closest, color) => Math.min(closest, colorDistance(color, candidateRgb)),
            Infinity,
        );
        if (separation > bestSeparation) {
            bestSeparation = separation;
            bestChroma = candidate;
        }
    });

    return bestChroma;
}

function pixelsMatch(a: Uint8ClampedArray, b: Uint8ClampedArray, offset: number): boolean {
    return (
        Math.abs(a[offset] - b[offset]) <= MASK_EPSILON &&
        Math.abs(a[offset + 1] - b[offset + 1]) <= MASK_EPSILON &&
        Math.abs(a[offset + 2] - b[offset + 2]) <= MASK_EPSILON
    );
}

/**
 * Walks both renders in lockstep and keeps the colors of the positions that did not change —
 * everything that is not a text glyph. Strokes, shadows, and design-drawn panels behind the
 * text are identical in both renders, so they stay part of the background.
 */
function collectUnchangedColors(
    imageA: ImageData,
    imageB: ImageData,
): { colors: RGB[]; textSampleFraction: number } {
    const a = imageA.data;
    const b = imageB.data;
    const colors: RGB[] = [];
    let sampled = 0;
    let differing = 0;

    for (let i = 0; i < a.length; i += 4 * SAMPLE_RATE) {
        sampled++;
        if (pixelsMatch(a, b, i)) {
            colors.push({ r: a[i], g: a[i + 1], b: a[i + 2] });
        } else {
            differing++;
        }
    }

    return { colors, textSampleFraction: sampled === 0 ? 0 : differing / sampled };
}

function imageDataToDataURL(imageData: ImageData): string | null {
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = imageData.width;
    tempCanvas.height = imageData.height;
    const tempCtx = tempCanvas.getContext('2d');
    if (!tempCtx) {
        return null;
    }
    tempCtx.putImageData(imageData, 0, 0);
    return tempCanvas.toDataURL();
}

function buildTextMaskImage(imageA: ImageData, imageB: ImageData): ImageData {
    const mask = new ImageData(imageA.width, imageA.height);
    for (let i = 0; i < imageA.data.length; i += 4) {
        const value = pixelsMatch(imageA.data, imageB.data, i) ? 0 : 255;
        mask.data[i] = value;
        mask.data[i + 1] = value;
        mask.data[i + 2] = value;
        mask.data[i + 3] = 255;
    }
    return mask;
}

/**
 * Gets the fill color from a text asset.
 * Returns the primary fill color as RGB.
 */
function getTextAssetColor(textAsset: TextAsset): RGB {
    // Get the fill color from the text asset
    // TextAssets may have color stored in different ways:
    // - textAsset.fillColor (direct property, decimal number)
    // - normalStyle.fill (palette index)
    // - A hex string
    // - An RGB object

    // get fill color
    const normalStyle = textAsset.normalStyle;
    const fillColor = normalStyle?.fill;

    // If it's a number, it's always a decimal color value (e.g., 0 for black, 14884386 for red, 16777215 for white)
    if (fillColor === undefined || fillColor === null) {
        throw new Error('Text asset has no fill color defined');
    }

    return decimalToRgb(fillColor);
}

/**
 * Renders the scene twice with the text asset carrying a different chroma each time, then
 * extracts the background colors from the text asset's bounds.
 *
 * A pixel is a text glyph if and only if it changed between the two renders, so the glyphs —
 * anti-aliased edges included — are identified exactly rather than guessed at by colour.
 * Filtering by colour instead, as this used to, throws away real background pixels whenever
 * the scene itself is close to the marker colour, which is what made pink and magenta scenes
 * report a contrast ratio that barely moved as the media was dimmed.
 *
 * Everything the design draws that does not move with the fill — strokes, shadows, panels
 * behind the text — is identical in both renders and stays part of the background.
 */
async function getBackgroundColorsForTextAsset(
    scene: Scene,
    textAsset: TextAsset,
    canvas: HTMLCanvasElement,
): Promise<RGB[]> {
    const { width, height } = scene.root.outputResolution;
    const { width: refWidth, height: refHeight } = scene.root.referenceResolution;

    // Ensure canvas is the correct size
    if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
    }

    const originalFillColor = textAsset.normalStyle.fill;

    try {
        // Fill colour does not affect layout, so this render settles the asset bounds just as
        // well as an untouched one would, and doubles as the first half of the diff.
        textAsset.normalStyle.fill = CHROMA_A;
        await scene.renderPreviewToCanvas(canvas, {
            subsceneIndex: 0,
        });

        let { x, y, width: assetWidth, height: assetHeight } = getEffectiveAssetBounds(textAsset);

        // Scale from reference resolution to output resolution
        const scaleX = width / refWidth;
        const scaleY = height / refHeight;

        x = x * scaleX;
        y = y * scaleY;
        assetWidth = assetWidth * scaleX;
        assetHeight = assetHeight * scaleY;

        // Get the canvas context
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            throw new Error('Could not get canvas context');
        }

        // If bounds are zero or invalid (e.g., in test environments), use the full canvas
        // In production, text assets from scene designs will have proper bounds
        if (assetWidth <= 0 || assetHeight <= 0) {
            x = 0;
            y = 0;
            assetWidth = width;
            assetHeight = height;
        }

        // Extract the pixel data from the text asset's bounds
        // Clamp the bounds to ensure they're within the canvas
        const clampedX = Math.max(0, Math.floor(x));
        const clampedY = Math.max(0, Math.floor(y));
        const clampedWidth = Math.min(width - clampedX, Math.ceil(assetWidth));
        const clampedHeight = Math.min(height - clampedY, Math.ceil(assetHeight));

        if (clampedWidth <= 0 || clampedHeight <= 0) {
            throw new Error('Text asset bounds are out of canvas bounds');
        }

        const imageA = ctx.getImageData(clampedX, clampedY, clampedWidth, clampedHeight);

        const chromaB = pickSecondChroma(imageA);
        textAsset.normalStyle.fill = chromaB;
        await scene.renderPreviewToCanvas(canvas, {
            subsceneIndex: 0,
        });
        const imageB = ctx.getImageData(clampedX, clampedY, clampedWidth, clampedHeight);

        const { colors, textSampleFraction } = collectUnchangedColors(imageA, imageB);

        const maskIsDegenerate =
            textSampleFraction > MAX_TEXT_SAMPLE_FRACTION || colors.length < MIN_BACKGROUND_SAMPLES;

        // Chroma B is by construction the candidate least present in this scene, so filtering
        // by distance to it is the best colour-based guess available when the mask is unusable.
        const chromaBRgb = decimalToRgb(chromaB);
        const backgroundColors = maskIsDegenerate
            ? extractColorsFromImageData(imageB, SAMPLE_RATE).filter(
                  (color) => !isNearColor(color, chromaBRgb, CHROMA_MATCH_TOLERANCE),
              )
            : colors;

        if (DEBUG_CONTRAST) {
            // eslint-disable-next-line no-console
            console.log(
                `[getBackgroundColorsForTextAsset] Sampled patch for "${textAsset.name}" (${clampedWidth}x${clampedHeight} at ${clampedX},${clampedY}):`,
                {
                    patchDataURL: imageDataToDataURL(imageA),
                    maskDataURL: imageDataToDataURL(buildTextMaskImage(imageA, imageB)),
                    chromaB: `#${chromaB.toString(16).padStart(6, '0')}`,
                    textSampleFraction,
                    usedDistanceFallback: maskIsDegenerate,
                    backgroundSampleCount: backgroundColors.length,
                },
            );
        }

        return backgroundColors;
    } finally {
        // Restore the original text color
        textAsset.normalStyle.fill = originalFillColor;
    }
}

/**
 * Graphics a text asset draws behind its own glyphs. A box or circle fills the area the text
 * sits on; a border draws an outline only, so whatever is underneath still shows through.
 */
export const FILLED_TEXT_GRAPHIC_TYPES = [VideoData.BOX_GRAPHIC, VideoData.CIRCLE_GRAPHIC] as const;

/** Roughly the cap height of a typical face, as a fraction of the em size. */
const GLYPH_HEIGHT_FRACTION = 0.7;

/**
 * How much of the text's own width a graphic must span before it reads as a backdrop rather
 * than a marker beside the glyphs. Generous, because a background is commonly inset from the
 * text box while an accent pill or bullet is a small fraction of it.
 */
const GLYPH_WIDTH_FRACTION = 0.5;

export type FilledTextGraphic = TextAsset['normalStyle']['graphics'][number] & {
    type: (typeof FILLED_TEXT_GRAPHIC_TYPES)[number];
};

export function isFilledTextGraphic(
    graphic: TextAsset['normalStyle']['graphics'][number],
): graphic is FilledTextGraphic {
    return FILLED_TEXT_GRAPHIC_TYPES.some((type) => type === graphic.type);
}

/**
 * A box graphic is only a *background* when it is sized to the text it belongs to. The same
 * graphic type is also how designs draw underline rules and accent bars — video 25 carries a
 * 4px-tall box pinned below the baseline — and those paint nothing behind the glyphs, so
 * calling one a backdrop sends the fix off after a shape the reader is not looking at.
 *
 * A fixed dimension is only plausible as a background if it is tall enough to sit behind the
 * glyphs rather than beside or beneath them.
 */
export function coversTheText(graphic: FilledTextGraphic, textAsset: TextAsset): boolean {
    if (graphic.type === VideoData.CIRCLE_GRAPHIC) {
        // A circle that resizes with the text wraps it; a fixed radius has to be big enough to
        // reach behind the glyphs rather than sit as a bullet or dot beside them.
        return graphic.radiusMode !== 'fixed' || reachesBehindGlyphs(graphic.radius * 2, textAsset);
    }
    const { boxHeight, boxWidth } = graphic;
    // Height and width are independent: a symbolic dimension tracks the text's own box and so
    // needs no measuring, but a fixed one does. A graphic only reads as a background when both
    // axes reach the glyphs — the disclaimer pill on the thrive/text_2a design tracks the text
    // box vertically and is a 30px sliver across, and calling that a background sends the fix
    // after a shape the reader is not looking at.
    const coversHeight = typeof boxHeight !== 'number' || reachesBehindGlyphs(boxHeight, textAsset);
    const coversWidth = typeof boxWidth !== 'number' || spansTheGlyphs(boxWidth, textAsset);
    return coversHeight && coversWidth;
}

/**
 * Whether a graphic of this height reaches behind the glyphs rather than sitting beside or
 * beneath them.
 *
 * textSize is the em size, and the letters themselves fill rather less of it — cap height is
 * around 70% for most faces. A graphic that reaches that far is behind the glyphs even though it
 * is shorter than the em, so measure against the glyphs rather than the em box.
 */
function reachesBehindGlyphs(height: number, textAsset: TextAsset): boolean {
    return height >= textAsset.textSize * GLYPH_HEIGHT_FRACTION;
}

/**
 * Whether a graphic of this width spans the glyphs rather than sitting beside them as an accent
 * pill, bullet, or marker.
 *
 * Unlike height there is no intrinsic scalar to measure against — textSize says nothing about
 * how wide a line runs — so measure the glyphs themselves.
 *
 * The asset's rendered bounds are no use here: a graphic is part of the asset, so a wide box
 * grows the bounds and a narrow one shrinks them, and either way the graphic is compared
 * against itself and always looks like it fits. maxWidth is the box the text is laid out
 * within, which the graphic cannot move.
 */
export function spansTheGlyphs(width: number, textAsset: TextAsset): boolean {
    const { maxWidth } = textAsset;
    if (maxWidth <= 0) {
        return true;
    }
    return width >= maxWidth * GLYPH_WIDTH_FRACTION;
}

/**
 * The same size tests for a graphic an asset group draws. A group's shape carries the geometry
 * as plain numbers — the configurator resolves symbolic dimensions such as INFERRED_MAX_HEIGHT
 * before the shape is loaded — so there is no symbolic case to allow through here.
 */
export function groupGraphicCoversTheText(
    graphic: AssetGroup['colorGraphics'][number],
    textAsset: TextAsset,
): boolean {
    const { shape } = graphic;
    if (graphic.graphic === VideoData.CIRCLE_GRAPHIC) {
        return reachesBehindGlyphs((shape.radius ?? 0) * 2, textAsset);
    }
    // A symbolic dimension (height, maxHeight, gapless) tracks the group's own bounds, so it is a
    // background by construction and there is no number to measure. Both axes have to reach the
    // glyphs: a group draws narrow accent pills with the same box it draws a panel with.
    const coversHeight =
        typeof shape.boxHeight !== 'number' || reachesBehindGlyphs(shape.boxHeight, textAsset);
    const coversWidth =
        typeof shape.boxWidth !== 'number' || spansTheGlyphs(shape.boxWidth, textAsset);
    return coversHeight && coversWidth;
}

/** Filled, switched on, and big enough to sit behind the glyphs. */
export function drawsBehindGlyphs(graphic: FilledTextGraphic, textAsset: TextAsset): boolean {
    return graphic.enabled && graphic.fillAlpha > 0 && coversTheText(graphic, textAsset);
}

const BACKDROP_ASSET_TYPES = [
    VideoData.MEDIA_ASSET_TYPE,
    VideoData.COLOR_ASSET_TYPE,
    VideoData.RECTANGLE_ASSET_TYPE,
    VideoData.CIRCLE_ASSET_TYPE,
    VideoData.GRADIENT_ASSET_TYPE,
] as const;

/**
 * A background panel drawn by one of the text's enclosing asset groups: the configurator, its
 * group, and its index within that group's `colorGraphics` (the address callers use to reach it).
 */
export interface GroupBackgroundGraphic {
    group: AssetGroup;
    /** Index into `group.colorGraphics`. */
    index: number;
    graphic: AssetGroup['colorGraphics'][number];
}

/**
 * Every filled colour graphic the text's enclosing asset group(s) draw — enabled, non-mask,
 * box/circle-shape `colorGraphics` — walked nearest-ancestor first, whatever their size. A group
 * draws a full background panel, a thin underline rule, and a narrow accent bar all with the same
 * kind of shape, so this returns them all; the caller decides which it cares about. `asMask`
 * graphics are skipped (they clip siblings rather than draw). `fillAlpha` 0 is kept here so contrast
 * detection can still see a fully-transparent shape; callers that only care about painted colour
 * (e.g. the colour map) filter those out themselves.
 *
 * A group's fill lives on `colorGraphics` (shape-nested) rather than the flat `normalStyle.graphics`
 * a text asset carries, so it needs its own read.
 */
export function findEnclosingGroupColorGraphics(textAsset: TextAsset): GroupBackgroundGraphic[] {
    // Walked as BaseAsset because a text asset's parent chain runs through internal containers
    // (the group's move wrapper, the subscene) that are not Assets, so the Asset-typed guards
    // cannot be applied to it directly.
    const results: GroupBackgroundGraphic[] = [];
    let ancestor: BaseAsset | null = textAsset.parent;
    while (ancestor) {
        if (ancestor.classType === VideoData.GROUP_ASSET_TYPE) {
            results.push(...getFilledGroupColorGraphics(ancestor as AssetGroup));
        }
        ancestor = ancestor.parent;
    }
    return results;
}

/**
 * The filled, enabled, non-mask box/circle `colorGraphics` a single asset group draws — the group's
 * own backdrops/underlines/accent bars, whatever their size. `asMask` graphics clip siblings rather
 * than draw and are skipped; `fillAlpha` 0 is kept so contrast detection can see a fully-transparent
 * shape (colour-map callers filter those out). This is the per-group primitive;
 * {@link findEnclosingGroupColorGraphics} walks a text's ancestors and unions their results.
 */
export function getFilledGroupColorGraphics(group: AssetGroup): GroupBackgroundGraphic[] {
    const results: GroupBackgroundGraphic[] = [];
    (group.colorGraphics ?? []).forEach((graphic, index) => {
        if (
            !graphic.asMask &&
            graphic.enabled &&
            FILLED_TEXT_GRAPHIC_TYPES.some((type) => type === graphic.graphic)
        ) {
            results.push({ group, index, graphic });
        }
    });
    return results;
}

/**
 * The subset of {@link findEnclosingGroupColorGraphics} that sits *behind the glyphs* — a real
 * background panel, not a thin underline or a narrow accent bar. This is what the contrast finder
 * wants: the thing the text is actually reading against.
 */
export function findEnclosingGroupBackgroundGraphics(
    textAsset: TextAsset,
): GroupBackgroundGraphic[] {
    return findEnclosingGroupColorGraphics(textAsset).filter(({ graphic }) =>
        groupGraphicCoversTheText(graphic, textAsset),
    );
}

/**
 * The background panel an enclosing asset group draws behind its children, if any — the first
 * covering panel that actually paints (`fillAlpha` > 0), nearest-ancestor first.
 */
function findEnclosingGroupBackground(textAsset: TextAsset): TextBackdrop | null {
    const visiblePanel = findEnclosingGroupBackgroundGraphics(textAsset).find(
        ({ graphic }) => (graphic.shape.fillAlpha ?? 0) > 0,
    );
    if (!visiblePanel) {
        return null;
    }
    return {
        assetId: visiblePanel.group.id,
        assetName: visiblePanel.group.name,
        kind: TextBackdropKind.GroupBackground,
    };
}

/**
 * Finds the backdrop the text is drawn on: of everything that overlaps it, the one drawn
 * last, since that is what the reader actually sees. Returns null when nothing overlaps.
 *
 * Uses approximateMaxBounds from getFreelyPositionedParent() like the validation code does.
 * getAssetsByType walks the tree in draw order, so a later entry sits on top of an earlier one.
 */
function findControllingBackdrop(scene: Scene, textAsset: TextAsset): TextBackdrop | null {
    // A background the text asset draws for itself is drawn closest to the glyphs, so it wins
    // over anything else in the scene. It is also not separately addressable: the fix is the text
    // colour or the graphic's own fill, never the media underneath, which the reader cannot see.
    const ownBackground = textAsset.normalStyle.graphics
        .filter(isFilledTextGraphic)
        .find((graphic) => drawsBehindGlyphs(graphic, textAsset));
    if (ownBackground) {
        return {
            assetId: textAsset.id,
            assetName: textAsset.name,
            kind: TextBackdropKind.TextOwnBackground,
        };
    }

    // A panel an enclosing group draws behind its children sits between the text and the scene,
    // so it is the backdrop just as much as one the text draws itself — and takes the same fix,
    // since dimming the media beneath a panel the reader is looking through changes nothing.
    const groupBackground = findEnclosingGroupBackground(textAsset);
    if (groupBackground) {
        return groupBackground;
    }

    // Get text bounds using the same approach as validation code
    const textBounds = textAsset.getFreelyPositionedParent().approximateMaxBounds;
    const textLeft = Math.floor(textBounds.x);
    const textRight = Math.ceil(textBounds.x + textBounds.width);
    const textTop = Math.floor(textBounds.y);
    const textBottom = Math.ceil(textBounds.y + textBounds.height);

    // The watermark is a small logo, not something text is laid on, and the agent cannot dim it
    // with edit_media, so naming it would send the fix after the wrong asset.
    const backdropAssets = BACKDROP_ASSET_TYPES.flatMap((assetType) =>
        scene.getAssetsByType(assetType),
    ).filter((asset) => !isWatermarkAsset(asset));
    const drawOrder = scene.getAssets();
    // Anything drawn after the text is in front of it, not behind: stickers in particular are
    // media assets that come after every scene-level asset, so one clipping a headline would
    // otherwise outrank the panel the text actually sits on.
    const textDrawIndex = drawOrder.indexOf(textAsset);

    let controlling: TextBackdrop | null = null;
    let controllingDrawIndex = -1;

    for (const backdropAsset of backdropAssets) {
        const assetBounds = getDrawnAssetBounds(backdropAsset);
        const assetLeft = Math.floor(assetBounds.x);
        const assetRight = Math.ceil(assetBounds.x + assetBounds.width);
        const assetTop = Math.floor(assetBounds.y);
        const assetBottom = Math.ceil(assetBounds.y + assetBounds.height);

        // Check for overlap using rectangle intersection (same logic as validation code)
        const deltaX = Math.min(textRight, assetRight) - Math.max(textLeft, assetLeft);
        const deltaY = Math.min(textBottom, assetBottom) - Math.max(textTop, assetTop);
        if (deltaX <= 0 || deltaY <= 0) {
            continue;
        }

        const drawIndex = drawOrder.indexOf(backdropAsset);
        if (drawIndex < controllingDrawIndex) {
            continue;
        }
        if (textDrawIndex !== -1 && drawIndex > textDrawIndex) {
            continue;
        }

        controllingDrawIndex = drawIndex;
        controlling = {
            assetId: backdropAsset.id,
            assetName: backdropAsset.name,
            kind:
                backdropAsset.classType === VideoData.MEDIA_ASSET_TYPE
                    ? TextBackdropKind.Media
                    : TextBackdropKind.ShapeBackdrop,
        };
    }

    return controlling;
}

/**
 * Calculates the contrast ratio between a text asset and its background in a scene.
 *
 * This function:
 * 1. Finds the backdrop the text is drawn on (skips if nothing overlaps it)
 * 2. Temporarily changes the text color to magenta (preserving all text graphics)
 * 3. Renders the scene to a canvas
 * 4. Extracts colors from the text asset's bounds, filtering out magenta pixels
 * 5. Calculates the average background color
 * 6. Gets the original text color
 * 7. Calculates and returns the contrast ratio using WCAG 2.0 formula
 *
 * @param scene - The Luminary scene containing the text asset
 * @param textAsset - The text asset to analyze
 * @param canvas - A canvas element to use for rendering (will be resized as needed)
 * @returns Promise resolving to TextContrastResult with contrast information, or null if nothing overlaps the text
 * @throws Error if text asset is not a valid text asset
 */
export async function calculateTextContrast(
    scene: Scene,
    textAsset: TextAsset,
    canvas: HTMLCanvasElement,
): Promise<TextContrastResult | null> {
    // Verify this is a text asset
    if (!VideoData.isTextAsset(textAsset)) {
        throw new Error('Asset is not a text asset');
    }

    // Skip if nothing draws a backdrop behind the text
    const backdrop = findControllingBackdrop(scene, textAsset);
    if (!backdrop) {
        return null;
    }

    // Get the text color
    const textColor = getTextAssetColor(textAsset);

    // Get the background colors
    const backgroundColors = await getBackgroundColorsForTextAsset(scene, textAsset, canvas);

    if (backgroundColors.length === 0) {
        throw new Error('No background colors found');
    }

    // Calculate average background color
    const backgroundColor = averageColor(backgroundColors);

    // Calculate contrast ratio
    const contrastRatio = calculateContrastRatio(textColor, backgroundColor);

    const fontSize = textAsset.textSize;

    return {
        contrastRatio,
        textColor,
        backgroundColor,
        textAssetId: textAsset.id,
        textAssetName: textAsset.name,
        textContent: textAsset.text || '',
        fontSize,
        isLargeText: isLargeFontSize(fontSize),
        backdrop,
    };
}

/**
 * Internal helper: Calculates the contrast ratio for all text assets in a scene.
 * This operates directly on a scene without isolation.
 *
 * @param scene - The Luminary scene to analyze
 * @param canvas - A canvas element to use for rendering (will be resized as needed)
 * @param options - Options for filtering text assets
 * @returns Promise resolving to array of TextContrastResult for each text asset
 */
export async function _calculateAllTextContrasts(
    scene: Scene,
    canvas: HTMLCanvasElement,
    options: {
        onlyEditable?: boolean;
    } = {},
): Promise<TextContrastResult[]> {
    const { onlyEditable = true } = options;

    // Get all text assets
    let textAssets = scene.getAssetsByType(VideoData.TEXT_ASSET_TYPE);

    // Filter to only editable text if requested
    if (onlyEditable) {
        textAssets = textAssets.filter((asset) => asset.isEditable);
    }

    // Calculate contrast for each text asset
    const results: TextContrastResult[] = [];

    for (const textAsset of textAssets) {
        if (textAsset.text.trim()) {
            const result = await calculateTextContrast(scene, textAsset, canvas);
            if (result !== null) {
                results.push(result);
            }
        }
    }

    return results;
}

/**
 * Calculates the contrast ratio for all text assets in all scenes of a Luminary video.
 *
 * @param luminary - The Luminary instance
 * @param canvas - A canvas element to use for rendering (will be resized as needed)
 * @param options - Options for filtering text assets
 * @returns Promise resolving to a map of sceneId -> array of TextContrastResult
 */
export async function calculateAllTextContrastsForVideo(
    luminary: Luminary,
    canvas: HTMLCanvasElement,
    options: {
        onlyEditable?: boolean;
    } = {},
): Promise<Map<string, TextContrastResult[]>> {
    const results = new Map<string, TextContrastResult[]>();

    for (const scene of luminary.scenes) {
        const sceneResults = await _calculateAllTextContrasts(scene, canvas, options);
        if (sceneResults.length > 0) {
            results.set(scene.id, sceneResults);
        }
    }

    return results;
}

/**
 * Extracts colors from ImageData, filtering by alpha channel.
 * Only includes pixels with alpha >= threshold (default 128, ~50% opacity).
 *
 * @param imageData - The ImageData to extract colors from
 * @param sampleRate - Sample every Nth pixel (default 10)
 * @param alphaThreshold - Minimum alpha value to include (0-255, default 128)
 * @returns Array of RGB colors for pixels above alpha threshold
 */
function extractColorsFromImageDataWithAlpha(
    imageData: ImageData,
    sampleRate = 10,
    alphaThreshold = 128,
): RGB[] {
    const colors: RGB[] = [];
    const { data, width, height } = imageData;

    for (let y = 0; y < height; y += sampleRate) {
        for (let x = 0; x < width; x += sampleRate) {
            const index = (y * width + x) * 4;
            const alpha = data[index + 3];

            // Only include pixels with sufficient opacity
            if (alpha >= alphaThreshold) {
                colors.push({
                    r: data[index],
                    g: data[index + 1],
                    b: data[index + 2],
                });
            }
        }
    }

    return colors;
}

/**
 * Calculates the average color of a watermark by sampling its pixels.
 * Excludes transparent pixels (alpha < 128).
 *
 * @param scene - The scene containing the watermark
 * @param watermark - The watermark MediaAsset
 * @param canvas - Canvas to render to
 * @returns Average RGB color of visible watermark pixels
 */
async function getWatermarkAverageColor(
    scene: Scene,
    watermark: MediaAsset,
    canvas: HTMLCanvasElement,
): Promise<RGB> {
    const { width, height } = scene.root.outputResolution;
    const { width: refWidth, height: refHeight } = scene.root.referenceResolution;

    // Ensure canvas is the correct size
    if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
    }

    // Render the scene with the watermark
    await scene.renderPreviewToCanvas(canvas, {
        subsceneIndex: 0,
    });

    // Get watermark bounds in output resolution
    const bounds = watermark.getFreelyPositionedParent().approximateMaxBounds;

    // Scale from reference resolution to output resolution
    const scaleX = width / refWidth;
    const scaleY = height / refHeight;

    const x = Math.max(0, Math.floor(bounds.x * scaleX));
    const y = Math.max(0, Math.floor(bounds.y * scaleY));
    const watermarkWidth = Math.min(width - x, Math.ceil(bounds.width * scaleX));
    const watermarkHeight = Math.min(height - y, Math.ceil(bounds.height * scaleY));

    if (watermarkWidth <= 0 || watermarkHeight <= 0) {
        throw new Error('Watermark bounds are out of canvas bounds');
    }

    // Get the canvas context and extract the watermark region
    const ctx = canvas.getContext('2d');
    if (!ctx) {
        throw new Error('Could not get canvas context');
    }

    const imageData = ctx.getImageData(x, y, watermarkWidth, watermarkHeight);

    // Log watermark patch for debugging
    if (DEBUG_CONTRAST) {
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = watermarkWidth;
        tempCanvas.height = watermarkHeight;
        const tempCtx = tempCanvas.getContext('2d');
        if (tempCtx) {
            tempCtx.putImageData(imageData, 0, 0);
            const watermarkPatchDataURL = tempCanvas.toDataURL();
            // eslint-disable-next-line no-console
            console.log(
                `[Watermark Contrast] Watermark patch (${watermarkWidth}x${watermarkHeight} at ${x},${y}):`,
                watermarkPatchDataURL,
            );
        }
    }

    // Sample watermark pixels, excluding transparent ones
    const sampleRate = 10;
    const colors = extractColorsFromImageDataWithAlpha(imageData, sampleRate, 128);

    if (colors.length === 0) {
        throw new Error('No visible watermark pixels found');
    }

    // Return average color
    return averageColor(colors);
}

/**
 * Gets the background colors behind a watermark by temporarily hiding it.
 *
 * @param scene - The scene containing the watermark
 * @param watermark - The watermark MediaAsset
 * @param luminary - The Luminary instance
 * @param canvas - Canvas to render to
 * @returns Array of RGB colors sampled from the background
 */
async function getBackgroundColorsForWatermark(
    scene: Scene,
    watermark: MediaAsset,
    luminary: Luminary,
    canvas: HTMLCanvasElement,
): Promise<RGB[]> {
    const { width, height } = scene.root.outputResolution;
    const { width: refWidth, height: refHeight } = scene.root.referenceResolution;

    // Ensure canvas is the correct size
    if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
    }

    // Get watermark bounds
    const bounds = watermark.getFreelyPositionedParent().approximateMaxBounds;

    // Scale from reference resolution to output resolution
    const scaleX = width / refWidth;
    const scaleY = height / refHeight;

    const x = Math.max(0, Math.floor(bounds.x * scaleX));
    const y = Math.max(0, Math.floor(bounds.y * scaleY));
    const watermarkWidth = Math.min(width - x, Math.ceil(bounds.width * scaleX));
    const watermarkHeight = Math.min(height - y, Math.ceil(bounds.height * scaleY));

    if (watermarkWidth <= 0 || watermarkHeight <= 0) {
        throw new Error('Watermark bounds are out of canvas bounds');
    }

    // Temporarily hide the watermark
    const originalVisible = luminary.template.watermark.visible;
    luminary.template.watermark.visible = false;

    try {
        // Render the scene without the watermark
        await scene.renderPreviewToCanvas(canvas, {
            subsceneIndex: 0,
        });

        // Get the canvas context
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            throw new Error('Could not get canvas context');
        }

        // Extract the pixel data from the watermark's bounds
        const imageData = ctx.getImageData(x, y, watermarkWidth, watermarkHeight);

        // Log background patch for debugging
        if (DEBUG_CONTRAST) {
            const tempCanvas = document.createElement('canvas');
            tempCanvas.width = watermarkWidth;
            tempCanvas.height = watermarkHeight;
            const tempCtx = tempCanvas.getContext('2d');
            if (tempCtx) {
                tempCtx.putImageData(imageData, 0, 0);
                const backgroundPatchDataURL = tempCanvas.toDataURL();
                // eslint-disable-next-line no-console
                console.log(
                    `[Watermark Contrast] Background patch (${watermarkWidth}x${watermarkHeight} at ${x},${y}):`,
                    backgroundPatchDataURL,
                );
            }
        }

        // Sample every 10th pixel for performance
        const sampleRate = 10;
        const colors = extractColorsFromImageData(imageData, sampleRate);

        return colors;
    } finally {
        // Restore the original watermark visibility
        luminary.template.watermark.visible = originalVisible;
    }
}

/**
 * Internal helper: Calculates the contrast ratio between a watermark and its background in a scene.
 * This operates directly on a scene without isolation.
 *
 * @param scene - The Luminary scene containing the watermark
 * @param luminary - The Luminary instance
 * @param canvas - A canvas element to use for rendering (will be resized as needed)
 * @returns Promise resolving to WatermarkContrastResult with contrast information, or null if no watermark
 */
export async function _calculateWatermarkContrast(
    scene: Scene,
    luminary: Luminary,
    canvas: HTMLCanvasElement,
): Promise<WatermarkContrastResult | null> {
    // Get the watermark asset
    const watermark = scene.getAssetByName('watermark_1');

    if (!watermark || !VideoData.isMediaAsset(watermark)) {
        return null; // No watermark
    }

    // Check if watermark is visible
    if (!luminary.template.watermark.visible) {
        return null;
    }

    // Get watermark color (average of visible pixels)
    const watermarkColor = await getWatermarkAverageColor(scene, watermark, canvas);

    // Get background colors
    const backgroundColors = await getBackgroundColorsForWatermark(
        scene,
        watermark,
        luminary,
        canvas,
    );

    if (backgroundColors.length === 0) {
        throw new Error('No background colors found');
    }

    // Calculate average background color
    const backgroundColor = averageColor(backgroundColors);

    // Calculate contrast ratio
    const contrastRatio = calculateContrastRatio(watermarkColor, backgroundColor);

    // Log final contrast result
    if (DEBUG_CONTRAST) {
        // eslint-disable-next-line no-console
        console.log('[Watermark Contrast] Final result:', {
            contrastRatio: contrastRatio.toFixed(2),
            watermarkColor,
            backgroundColor,
            position: luminary.template.watermark.position,
            opacity: luminary.template.watermark.opacity,
        });
    }

    return {
        contrastRatio,
        watermarkColor,
        backgroundColor,
        watermarkPosition: luminary.template.watermark.position,
        watermarkOpacity: luminary.template.watermark.opacity,
    };
}

/**
 * Calculates the contrast ratio for a watermark in a scene using an isolated Luminary instance.
 * This ensures that the calculations don't affect the main Luminary instance.
 *
 * @param luminary - The source Luminary instance (will not be modified)
 * @param sceneId - The ID of the scene to analyze
 * @returns Promise resolving to WatermarkContrastResult, or null if no watermark
 */
export async function calculateWatermarkContrast(
    luminary: Luminary,
    sceneId: string,
): Promise<WatermarkContrastResult | null> {
    // Create an isolated Luminary instance
    const isolatedLuminary = new Luminary({
        fetchResources: {
            audio: false,
            media: true,
            transitions: false,
            fonts: true,
        },
        disableRenderer: false,
        resolutionValue: luminary.resolutionValue,
    });

    try {
        // Load video data into the isolated instance
        const videoData = luminary.save();
        isolatedLuminary.load(videoData);
        await awaitCanPlayWithTimeout(isolatedLuminary);

        // Find the scene in the isolated instance by ID
        const isolatedScene = isolatedLuminary.getSceneById(sceneId);

        if (!isolatedScene) {
            throw new Error(`Scene with ID ${sceneId} not found in isolated Luminary instance`);
        }

        // Create a canvas for rendering
        const canvas = document.createElement('canvas');

        // Calculate watermark contrast using the internal helper
        const result = await _calculateWatermarkContrast(isolatedScene, isolatedLuminary, canvas);

        return result;
    } finally {
        // Always destroy the isolated instance to free resources
        isolatedLuminary.destroy();
    }
}

/**
 * Checks if there is a watermark on the scene that overlaps with any editable media assets.
 * Returns true if watermark exists, is visible, and intersects with at least one editable media asset.
 */
function hasWatermarkOverlappingMediaAssets(scene: Scene, luminary: Luminary): boolean {
    // Check if watermark exists and is visible
    if (!luminary.template.watermark.visible) {
        return false;
    }

    // Get the watermark asset
    const watermark = scene.getAssetByName('watermark_1');
    if (!watermark || !VideoData.isMediaAsset(watermark)) {
        return false;
    }

    // Get watermark bounds
    const watermarkBounds = watermark.getFreelyPositionedParent().approximateMaxBounds;
    const watermarkLeft = Math.floor(watermarkBounds.x);
    const watermarkRight = Math.ceil(watermarkBounds.x + watermarkBounds.width);
    const watermarkTop = Math.floor(watermarkBounds.y);
    const watermarkBottom = Math.ceil(watermarkBounds.y + watermarkBounds.height);

    // Get all editable media assets
    const mediaAssets = scene
        .getAssetsByType(VideoData.MEDIA_ASSET_TYPE)
        .filter((asset) => asset.isEditable);

    // Check if watermark overlaps with any media asset
    for (const mediaAsset of mediaAssets) {
        const mediaBounds = mediaAsset.getFreelyPositionedParent().approximateMaxBounds;
        const mediaLeft = Math.floor(mediaBounds.x);
        const mediaRight = Math.ceil(mediaBounds.x + mediaBounds.width);
        const mediaTop = Math.floor(mediaBounds.y);
        const mediaBottom = Math.ceil(mediaBounds.y + mediaBounds.height);

        // Check for overlap using rectangle intersection
        const deltaX = Math.min(watermarkRight, mediaRight) - Math.max(watermarkLeft, mediaLeft);
        const deltaY = Math.min(watermarkBottom, mediaBottom) - Math.max(watermarkTop, mediaTop);
        const overlaps = deltaX > 0 && deltaY > 0;

        if (overlaps) {
            return true;
        }
    }

    return false;
}

/**
 * Result from testing multiple watermark options for contrast.
 */
export interface WatermarkContrastOption {
    watermarkIndex: number;
    contrastResult: WatermarkContrastResult;
}

/**
 * Calculates contrast for all available watermark options using a pre-created isolated Luminary instance.
 * Use this when processing multiple scenes to avoid creating/destroying WebGL contexts repeatedly.
 *
 * @param isolatedLuminary - A pre-created isolated Luminary instance (caller is responsible for cleanup)
 * @param sceneId - The ID of the scene to analyze
 * @returns Promise resolving to array of contrast results for each watermark option, or empty array if not applicable
 */
export async function calculateWatermarkContrastsForAllOptionsWithLuminary(
    isolatedLuminary: Luminary,
    sceneId: string,
): Promise<WatermarkContrastOption[]> {
    // Only calculate if template has exactly 2 watermarks
    if (isolatedLuminary.template.watermarkSources.length !== 2) {
        return [];
    }

    // Find the scene in the isolated instance by ID
    const isolatedScene = isolatedLuminary.getSceneById(sceneId);

    if (!isolatedScene) {
        return [];
    }

    // Only calculate if watermark exists and overlaps with editable media
    if (!hasWatermarkOverlappingMediaAssets(isolatedScene, isolatedLuminary)) {
        return [];
    }

    // Create a canvas for rendering
    const canvas = document.createElement('canvas');

    // Calculate contrast for each watermark option
    const results: WatermarkContrastOption[] = [];

    for (let watermarkIndex = 0; watermarkIndex < 2; watermarkIndex++) {
        // Set the watermark to test
        isolatedScene.watermark.sourceData =
            VideoData.templateWatermarkSourcesByIndex(watermarkIndex);

        // Calculate contrast for this watermark
        const contrastResult = await _calculateWatermarkContrast(
            isolatedScene,
            isolatedLuminary,
            canvas,
        );

        // Only include results where contrast was successfully calculated
        if (contrastResult !== null) {
            results.push({
                watermarkIndex,
                contrastResult,
            });
        }
    }

    return results;
}

/**
 * Calculates contrast for all available watermark options in a template.
 * Only works with templates that have exactly 2 watermarks, a visible watermark on the scene,
 * and the watermark overlaps with at least one editable media asset.
 *
 * @param luminary - The source Luminary instance (will not be modified)
 * @param sceneId - The ID of the scene to analyze
 * @returns Promise resolving to array of contrast results for each watermark option, or empty array if not applicable
 */
export async function calculateWatermarkContrastsForAllOptions(
    luminary: Luminary,
    sceneId: string,
): Promise<WatermarkContrastOption[]> {
    // Only calculate if template has exactly 2 watermarks
    if (luminary.template.watermarkSources.length !== 2) {
        return [];
    }

    // Find the scene to check for watermark overlap
    const scene = luminary.scenes.find((s) => s.id === sceneId);
    if (!scene) {
        return [];
    }

    // Only calculate if watermark exists and overlaps with editable media
    if (!hasWatermarkOverlappingMediaAssets(scene, luminary)) {
        return [];
    }

    // Create an isolated Luminary instance
    const isolatedLuminary = new Luminary({
        fetchResources: {
            audio: false,
            media: true,
            transitions: false,
            fonts: true,
        },
        disableRenderer: false,
        resolutionValue: luminary.resolutionValue,
    });

    try {
        // Load video data into the isolated instance
        const videoData = luminary.save();
        isolatedLuminary.load(videoData);
        await awaitCanPlayWithTimeout(isolatedLuminary);

        // Use the shared implementation
        return calculateWatermarkContrastsForAllOptionsWithLuminary(isolatedLuminary, sceneId);
    } finally {
        // Always destroy the isolated instance to free resources
        isolatedLuminary.destroy();
    }
}

// Expose utility for debugging in browser console
if (typeof window !== 'undefined') {
    (window as any).calculateAllTextContrastsForVideo = calculateAllTextContrastsForVideo;

    (window as any).calculateWatermarkContrast = calculateWatermarkContrast;

    (window as any).calculateWatermarkContrastsForAllOptions =
        calculateWatermarkContrastsForAllOptions;
}
